import { PayloadBatchOperationType } from '@surglogs/util'

import { Pagination } from '../types'

export enum RemindersEditorReminderUnitValue {
  DAYS = 'DAYS',
  WEEKS = 'WEEKS',
  MONTHS = 'MONTHS',
  YEARS = 'YEARS',
}

export type RemindersEditorReminderAPIEntity = {
  id: number
  unit: RemindersEditorReminderUnitValue
  amount: number
}

export enum CredentialingDocumentSignatureStatus {
  NONE = 'NONE',
  REQUESTED = 'REQUESTED',
  SIGNED = 'SIGNED',
}

export enum CredentialingDocumentStatus {
  ACTIVE = 'ACTIVE',
  EXPIRING = 'EXPIRING',
  EXPIRED = 'EXPIRED',
  ARCHIVED = 'ARCHIVED',
  NO_FILES = 'NO_FILES',
  SYSTEM_ARCHIVED = 'SYSTEM_ARCHIVED',
  IGNORED = 'IGNORED',
  INCOMPLETE = 'INCOMPLETE',
}

export enum CredentialingDocumentVerificationStatus {
  NOT_AVAILABLE = 'N/A',
  VERIFIED = 'VERIFIED',
  UNVERIFIED = 'UNVERIFIED',
}

export type CredentialingDocumentSignature = {
  user_name: string
  email: string
  phone?: string | null
  signed_at: string | null
}

export type CredentialingClinicReminderAPIEntity = {
  id?: string | number
  amount: number
  unit: RemindersEditorReminderUnitValue
  default?: boolean
}

export type CredentialingClinicUserDocGetResponse = {
  archived_date: string | null
  expiration_date: string | null
  ignored_date: string | null
  id: number
  fixed: boolean
  last_file_id: number | null
  folder_id: number
  url: string
  name: string
  reminders: CredentialingClinicReminderAPIEntity[]
  signature_state: CredentialingDocumentSignatureStatus
  state: CredentialingDocumentStatus
  type: 'DOCUMENTS'
  verification_state: CredentialingDocumentVerificationStatus
  verified_file_name: string | null
  verified_file_url: string | null
  verified_file_thumbnail_url: string | null
  form_id?: number | null
  form_entry_id?: number | null
  form_name?: string | null

  // only available in v2
  signatures?: CredentialingDocumentSignature[] | null
}

export type CredentialingClinicUserDocsGetQuery = {
  limit?: number
  offset?: number
  status?: CredentialingDocumentStatus | CredentialingDocumentStatus[]
  search?: string
  ignored?: boolean
  folder_id?: number
  cursor?: string
}

export type CredentialingClinicUserDocsGetResponse = {
  documents: CredentialingClinicUserDocGetResponse[]
  pagination: Pagination
}

export type CredentialingClinicUserDocsPostResponse =
  CredentialingClinicUserDocGetResponse

export type CredentialingClinicUserDocsPostPayload = {
  expiration_date?: string
  name: string
  url?: string
  reminders?: CredentialingClinicReminderAPIEntity[]
  verification_state?: CredentialingDocumentVerificationStatus
  verified_file_url?: string
  verified_file_name?: string
  folder_id?: number | null
  form_ids?: number[]
}

export type CredentialingClinicUserDocPatchResponse =
  CredentialingClinicUserDocGetResponse

export type CredentialingClinicUserDocPatchPayload =
  Partial<CredentialingClinicUserDocsPostPayload> & {
    // not boolean, endpoint works only with false
    ignored?: false
    state?: CredentialingDocumentStatus
  }

export enum CredentialingDocumentFileStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  SYSTEM_ARCHIVED = 'SYSTEM_ARCHIVED',
  EXPIRED = 'EXPIRED',
  EXPIRING = 'EXPIRING',
  PERMANENT = 'PERMANENT',
}

export interface CredentialingDocumentFileAPIEntity {
  id: number
  state: CredentialingDocumentFileStatus
  expiration_date: null | undefined | string
  archived_date: null | undefined | string
  created_date: string
  final_url: string
  url: string
  notes_count: number
  signatures: CredentialingDocumentSignature[] | undefined | null
  verified_file_url: null | undefined | string
  signature_state: CredentialingDocumentSignatureStatus
  thumbnail_url: string
}

export type CredentialingClinicUserDocFilesGetQuery = {
  limit?: number
  offset?: number
}

export type CredentialingClinicUserDocFilesGetResponse = {
  files: CredentialingDocumentFileAPIEntity[]
  pagination: Pagination
}

export type CredentialingClinicUserDocFilesPostResponse =
  CredentialingClinicUserDocGetResponse

export type CredentialingClinicUserDocFilesPostPayload = {
  expiration_date?: string
  name: string
  url?: string
  reminders?: CredentialingClinicReminderAPIEntity[]
  verification_state?: CredentialingDocumentVerificationStatus
  verified_file_url?: string
  verified_file_name?: string
}

export interface CredentialingDocumentFileNoteAPIEntity {
  id: number
  date: string
  text: string
  user_id: number
}

export type CredentialingClinicUserDocFileNotesGetResponse = {
  notes: CredentialingDocumentFileNoteAPIEntity[]
}

export type CredentialingClinicUserDocFileNotesPostResponse =
  CredentialingDocumentFileNoteAPIEntity

export type CredentialingClinicUserDocFileNotesPostPayload = {
  text: string
}

export type CredentialingClinicUserDocSignatureGetResponse = {
  email: string
  id: number
  state: CredentialingDocumentSignatureStatus
  user_id: number
  user_name: string
  original_url: string
  annotation: string
  phone: string
}

export type CredentialingClinicUserDocSignaturesGetResponse = {
  signatures: CredentialingClinicUserDocSignatureGetResponse[]
}

export type CredentialingClinicUserDocSignaturesPostPayload = {
  signatures: {
    user_name: string
    email?: string | null
    phone?: string | null
    user_id?: number
  }[]
}

export type CredentialingRoleAPIEntity = {
  id: number
  name: string
  fixed: boolean | undefined
}

export type CredentialingClinicRoleGetResponse = CredentialingRoleAPIEntity

export type CredentialingClinicRolesGetQuery = {
  limit?: number
  offset?: number
  search?: string
  cursor?: string
}
export type CredentialingClinicRolesGetResponse = {
  roles: CredentialingRoleAPIEntity[]
  pagination: {
    limit: number
    offset: number
    total: number
  }
}

export type CredentialingRoleTemplateAPIEntity = {
  id: number
  fixed?: boolean
  required?: boolean
  name: string
}

export type CredentialingClinicRolesPostPayload = Pick<
  CredentialingRoleAPIEntity,
  'name'
>
export type CredentialingClinicRolesPostResponse = CredentialingRoleAPIEntity

export type CredentialingClinicRolePutPayload = Pick<
  CredentialingRoleAPIEntity,
  'name'
>
export type CredentialingClinicRolePutResponse = CredentialingRoleAPIEntity

export type CredentialingClinicRoleTemplatesGetResponse = {
  templates: CredentialingRoleTemplateAPIEntity[]
}

export type CredentialingClinicRoleTemplatesPostResponse = {
  templates: CredentialingRoleTemplateAPIEntity[]
}

export type CredentialincClinicRoleTemplatesPostPayload = {
  templates: ({
    operation?: 'ADD' | 'UPDATE' | 'DELETE'
  } & Partial<CredentialingRoleTemplateAPIEntity>)[]
}

export type CredentialingFolderAPIEntity = {
  id: number
  fixed?: boolean
  name?: string
  order?: number
  templates?: number[]
}

export enum FolderOperationType {
  ADD = 'ADD',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export type CredentialingFolderPostPayload = CredentialingFolderAPIEntity & {
  operation: FolderOperationType
}

export type CredentialingClinicRoleFoldersPostPayload = {
  folders: CredentialingFolderPostPayload[]
}

export type CredentialingClinicRoleFoldersPostResponse = {
  folders: CredentialingFolderAPIEntity[]
}

export type CredentialingReminderAPIEntity = {
  id: number
  unit: RemindersEditorReminderUnitValue
  amount: number
}

export type CredentialingClinicRemindersGetResponse = {
  reminders: CredentialingReminderAPIEntity[]
}

export type CredentialingClinicRemindersPostPayload = Omit<
  CredentialingReminderAPIEntity,
  'id'
>
export type CredentialingClinicRemindersPostResponse =
  CredentialingReminderAPIEntity

export type CredentialingClinicUsersDocsGetQuery = {
  limit?: number
  offset?: number
  search: string
  disabled?: boolean
  credentialing_disabled?: boolean
  roles?: string[]
  cursor: string
}

export type CredentialingClinicUsersDoc =
  CredentialingClinicUserDocGetResponse & {
    user_id: number
    user_name?: string
  }

export type CredentialingClinicUsersDocsGetResponse = {
  items: CredentialingClinicUsersDoc[]
  pagination: Pagination
}

export type CredentialingClinicNeedAttentionGetQuery = {
  roles?: string[]
  limit?: number
  offset?: number
  cursor?: string
}

export type CredentialingClinicNeedAttentionGetResponse = {
  items: CredentialingClinicUsersDoc[]
  pagination: Pagination
}

export enum CredentialingReportsOverallDocumentStatus {
  ACTIVE = 'ACTIVE',
  NO_FILES = 'NO_FILES',
  EXPIRED = 'EXPIRED',
  EXPIRING = 'EXPIRING',
  ARCHIVED = 'ARCHIVED',
  SIGNED = 'SIGNED',
  REQUESTED = 'REQUESTED',
  VERIFIED = 'VERIFIED',
  UNVERIFIED = 'UNVERIFIED',
  N_A = 'N/A',
}

export enum CredentialingReportsQueryType {
  SIGNATURE = 'SIGNATURES',
  EXPIRATION = 'EXPIRATION',
  VERIFICATION = 'VERIFICATION',
}

export interface CredentialingClinicReportsOverallNode {
  count: number
  id: CredentialingReportsOverallDocumentStatus
}

export type CredentialingClinicGraphOverallGetQuery = {
  type?: CredentialingReportsQueryType
}

export type CredentialingClinicGraphOverallGetResponse = {
  nodes: CredentialingClinicReportsOverallNode[]
}

export interface CredentialingReportsItemAPIEntity {
  doc_id: number
  doc_name: string
  expiration_date: string
  signature_state: CredentialingDocumentSignatureStatus
  state: CredentialingDocumentStatus
  user_id: number
  user_name: string
  verification_state: CredentialingDocumentVerificationStatus
}

export type CredentialingClinicGraphOverallItemsGetQuery = {
  type?: CredentialingReportsQueryType
  roles?: string[]
}

export type CredentialingClinicGraphOverallItemsGetResponse = {
  pagination: Pagination
  items: CredentialingReportsItemAPIEntity[]
}

export type CredentialingClinicExportGetQuery = {
  date_from?: string
  date_to?: string
  export_view: 'HORIZONTAL' | 'VERTICAL'
}

export type CredentialingClinicUserFoldersGetResponse = {
  folders: CredentialingFolderAPIEntity[]
}

export type CredentialingClinicRoleFoldersGetResponse = {
  folders: CredentialingFolderAPIEntity[]
}

export type CredentialingClinicFilesPostPayload = {
  items: {
    user_id: number
    doc_id: number
    data: {
      expiration_date?: string | null
      file_id: string
      reminders?: Omit<CredentialingClinicReminderAPIEntity, 'id'>[]
    }
  }[]
  prevent_renew?: boolean
}

export interface DocumentsClinicFillableFormAPIEntity {
  id: number
  name: string
}

export type DocumentsClinicFillableFormsGetQuery = {
  limit?: number
  offset?: number
  search?: string
}

export type DocumentsClinicFillableFormsGetResponse = {
  items: DocumentsClinicFillableFormAPIEntity[]
  pagination: Pagination
}

export interface DocumentsClinicFillableFormsParameterPayload {
  name: string
  type: string
  is_required: boolean
  order: number
  choices?: { choice: string }[]
  unit?: string
  min_range?: string | number | null
  max_range?: string | number | null
  saved_list_id?: number
  is_multiselect?: boolean
  pretty_name?: string
  parameter_id?: number
}

export interface DocumentsClinicFillableFormsPostPayload {
  name: string
  parameters: DocumentsClinicFillableFormsParameterPayload[]
}

export interface DocumentsClinicFillableFormParameterAPIEntity {
  parameter_id: number
  parameter_version_id: number
  name: string
  type: string
  is_required: boolean
  order: number
  choices?: { id: number; choice: string }[] | null
  min_range?: string | null
  max_range?: string | null
  saved_list_id?: number | null
  is_multiselect?: boolean
  pretty_name?: string
}

export interface DocumentsClinicFillableFormGetResponse {
  id: number
  name: string
  parameters: DocumentsClinicFillableFormParameterAPIEntity[]
}

export interface DocumentsClinicFillableFormPutParameterPayload {
  parameter_id?: number
  operation: PayloadBatchOperationType
  name?: string
  type?: string
  is_required?: boolean
  order?: number
  choices?: { choice: string }[]
  unit?: string
  min_range?: string | number | null
  max_range?: string | number | null
  saved_list_id?: number
  is_multiselect?: boolean
  pretty_name?: string
}

export interface DocumentsClinicFillableFormPutPayload {
  name: string
  parameters: DocumentsClinicFillableFormPutParameterPayload[]
}

export interface DocumentsClinicFillableFormsImportPostPayload {
  srns: string[]
}

export interface DocumentsClinicFillableFormsImportPostResponse {
  created_form_ids: number[]
  failed_srns: string[]
}

export interface DocumentsClinicFillableFormEntryItemPayload {
  parameter_id: number
  value: string | null
}

export interface DocumentsClinicFillableFormEntryPutPayload {
  items: DocumentsClinicFillableFormEntryItemPayload[]
}

export interface DocumentsClinicFillableFormEntryParameterResponse
  extends DocumentsClinicFillableFormParameterAPIEntity {
  value: string | null
}

export interface DocumentsClinicFillableFormEntryGetResponse {
  id: number
  form_id: number
  form_name: string
  parameters: DocumentsClinicFillableFormEntryParameterResponse[]
}
